import { useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';

import { AuthResultPage } from 'pages/auth-result/auth-result-page';
import { DeadlineTrackerPage } from 'pages/deadline-tracker/deadline-tracker-page';
import { EstateDashboard } from 'pages/estate-dashboard/estate-dashboard';
import { IntegrationsPage } from 'pages/integrations-page';
import { ManageConditionalTemplateQuestionSetsPage } from 'pages/manage-conditional-template-question-sets/manage-conditional-template-question-sets-page';
import { ManageConditionalTemplateQuestionsPage } from 'pages/manage-conditional-template-questions/manage-conditional-template-questions-page';
import TemplateDataWizardPage from 'pages/template-data-wizard-page';
import TemplateDocumentPage from 'pages/template-document-page';
import TemplateQuestionnairePage from 'pages/template-questionnaire-page';

import PrivateRoute from './common/private-route';
import PublicRoute from './common/public-route';
import AssignedTasksPage from './pages/assigned-tasks-page';
import ConciergeAccounts from './pages/concierge-accounts';
import CorporateReferrals from './pages/corporate-referrals';
import Coupon from './pages/coupons';
import Dashboard from './pages/dashboard';
import IntakeForm from './pages/dashboard/components/intake-form-page';
import EditConcierge from './pages/edit-concierge';
import EditReferral from './pages/edit-referral';
import ErrorPage from './pages/error-page';
import EstateAccountingSummaryPage from './pages/estate-accounting-summary-page';
import EstateBalanceSheetPage from './pages/estate-balance-sheet-page';
import { EstateChecklist } from './pages/estate-checklist/estate-checklist';
import EstateResultRulesPage from './pages/estate-result-rules';
import EstateResultsPage from './pages/estate-results-page';
import EstateTaskTemplatesPage from './pages/estate-task-templates';
import EstateVault from './pages/estate-vault';
import HomePage from './pages/home-page';
import LoginPage from './pages/login-page';
import LoginRedirect from './pages/login-redirect';
import ManageConditionalTemplateAnswers from './pages/manage-conditional-template-answers';
import ManageConditionalTemplateFilters from './pages/manage-conditional-template-filters';
import ManageDocumentGenerationTemplates from './pages/manage-document-generation-templates';
import ManageEstateInputDataFormatListOption from './pages/manage-input-data-format-list-option';
import MyProfile from './pages/my-profile';
import NewConcierge from './pages/new-concierge';
import NewReferral from './pages/new-referral';
import NotFoundPage from './pages/not-found-page';
import PortalAccount from './pages/portal-account';
import ResetPasswordFormPage from './pages/reset-password-form-page';
import ResetPasswordPage from './pages/reset-password-page';
import SignUpPage from './pages/sign-up-page';
import StartPage from './pages/start-page';
import SubscriptionCheckoutPage from './pages/subscription-checkout-page';
import SubscriptionCheckoutSuccessPage from './pages/subscription-checkout-success-page';
import SubscriptionPricingTablePage from './pages/subscription-pricing-table-page';
import SuperAdminDocumentTags from './pages/superadmin-document-tags';
import SuperAdminEstates from './pages/superadmin-estates';
import SuperAdminEstateResultItems from './pages/superadmin-estates/result-items';
import SuperAdminEstateTasks from './pages/superadmin-estates/tasks';
import SuperAdminRequiredInfoFormat from './pages/superadmin-required-info-format';
import SuperAdminTasks from './pages/superadmin-tasks';
import VaultPage from './pages/vault-page';
import VerifyEmailFormPage from './pages/verify-email-form-page';
import VerifyEmailPage from './pages/verify-email-page';
import PrivateShared from './shared/private-shared';
import PublicShared from './shared/public-shared';

function Routes(): JSX.Element {
  const analytics = useAnalytics();
  const history = useHistory();

  function trackPageView() {
    analytics.page();
  }

  useEffect(() => {
    trackPageView(); // To track the first pageview upon load
    history.listen(trackPageView); // To track the subsequent pageviews

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <Switch>
      <PublicRoute exact path="/" component={LoginPage} />
      <PrivateRoute exact path="/home" component={LoginRedirect} />
      <Route exact path="/verify-email" component={VerifyEmailPage} />
      <Route path="/verify-email/:id/:verificationCode?" component={VerifyEmailFormPage} />
      <PublicRoute exact path="/reset-password" component={ResetPasswordPage} />
      <PublicRoute path="/reset-password/:id" component={ResetPasswordFormPage} />
      <PublicRoute path="/sign-up" component={SignUpPage} />
      <PublicRoute path="/start" component={StartPage} />
      <PublicRoute path="/vault/shared/:id" component={PublicShared} />
      <PrivateRoute exact path="/home/:portalId/deadline-tracker" component={DeadlineTrackerPage} />
      <PrivateRoute path="/home/:portalId/vault/shared/:id" component={PrivateShared} />
      <PrivateRoute exact path="/home/:portalId/vault" component={VaultPage} />
      <PrivateRoute exact path="/home/:portalId/vault/:estateId/:folder" component={EstateVault} />
      <PrivateRoute exact path="/home/:portalId/home" component={HomePage} />
      <PrivateRoute exact path="/home/:portalId/dashboard" component={Dashboard} />
      <PrivateRoute exact path="/home/:portalId/intake-form" component={IntakeForm} />
      <PrivateRoute exact path="/home/:portalId/my-profile" component={MyProfile} />
      <PrivateRoute exact path="/home/:portalId/portal-account" component={PortalAccount} />
      <PrivateRoute exact path="/home/:portalId/assigned-tasks" component={AssignedTasksPage} />
      <PrivateRoute
        exact
        path="/home/:portalId/estate/:estateId/estate-balance-sheet"
        component={EstateBalanceSheetPage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/task/:estateWorkItemId/template-questionnaire"
        component={TemplateQuestionnairePage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/task/:estateWorkItemId/template-data-wizard"
        component={TemplateDataWizardPage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/task/:estateWorkItemId/template-document"
        component={TemplateDocumentPage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/estate/:estateId/estate-accounting-summary"
        component={EstateAccountingSummaryPage}
      />
      <PrivateRoute exact path="/home/referrals" component={CorporateReferrals} />
      <PrivateRoute exact path="/home/new-referral" component={NewReferral} />
      <PrivateRoute exact path="/home/:selectedPartnerId/edit-referral" component={EditReferral} />
      <PrivateRoute exact path="/home/concierges" component={ConciergeAccounts} />
      <PrivateRoute exact path="/home/:userId/edit-concierge" component={EditConcierge} />
      <PrivateRoute exact path="/home/new-concierge" component={NewConcierge} />
      <PrivateRoute exact path="/admin/estates" component={SuperAdminEstates} />
      <PrivateRoute
        exact
        path="/admin/estates/:estateId/result-items"
        component={SuperAdminEstateResultItems}
      />
      <PrivateRoute exact path="/admin/tasks" component={SuperAdminTasks} />
      <PrivateRoute exact path="/admin/estates/:estateId/tasks" component={SuperAdminEstateTasks} />
      <PrivateRoute exact path="/home/coupons" component={Coupon} />
      <PrivateRoute exact path="/home/estate-result-rules" component={EstateResultRulesPage} />
      <PrivateRoute exact path="/home/estate-task-templates" component={EstateTaskTemplatesPage} />
      <PrivateRoute
        exact
        path="/home/:conditionalTemplateQuestionSetId/:conditionalTemplateQuestionId/manage-conditional-template-answers"
        component={ManageConditionalTemplateAnswers}
      />
      <PrivateRoute
        exact
        path="/home/:conditionalTemplateQuestionSetId/:conditionalTemplateQuestionId/manage-conditional-template-filters"
        component={ManageConditionalTemplateFilters}
      />
      <PrivateRoute
        exact
        path="/home/manage-conditional-template-question-sets"
        component={ManageConditionalTemplateQuestionSetsPage}
      />
      <PrivateRoute
        exact
        path="/home/:conditionalTemplateQuestionSetId/manage-conditional-template-questions"
        component={ManageConditionalTemplateQuestionsPage}
      />
      <PrivateRoute exact path="/home/manage-document-tags" component={SuperAdminDocumentTags} />
      <PrivateRoute
        exact
        path="/home/manage-document-generation-templates"
        component={ManageDocumentGenerationTemplates}
      />
      <PrivateRoute
        exact
        path="/home/:estateInputDataFormatId/manage-estate-input-data-format-list-option"
        component={ManageEstateInputDataFormatListOption}
      />

      <PrivateRoute
        exact
        path="/home/manage-required-information-format"
        component={SuperAdminRequiredInfoFormat}
      />
      <PrivateRoute exact path="/home/:portalId/estate/:estateId" component={EstateChecklist} />
      <PrivateRoute
        exact
        path="/home/:portalId/estate/:estateId/estate-results"
        component={EstateResultsPage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/subscription-pricing-table"
        component={SubscriptionPricingTablePage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/subscription-checkout"
        component={SubscriptionCheckoutPage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/subscription-checkout-success"
        component={SubscriptionCheckoutSuccessPage}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/estate/:estateId/estate-dashboard/:tab?"
        component={EstateDashboard}
      />
      <PrivateRoute
        exact
        path="/home/:portalId/portal-account/integrations"
        component={IntegrationsPage}
      />
      <PrivateRoute exact path="/home/auth-result" component={AuthResultPage} />

      <Route path="*/error" component={ErrorPage} />
      <Route path="*" component={NotFoundPage} />
    </Switch>
  );
}

export default Routes;
